import type { HTMLAttributes } from "react";

import { CircleCheckBig, MessageCircleQuestion, Star } from "lucide-react";

import { ColorDot } from "@/components/cinch/ColorDot";
import { StickyNote } from "@/components/cinch/StickyNote";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import type { Note } from "../api";
import { BOARD_META, impactCategoryMeta, type StickyTone } from "../constants";

/** One sticky note anywhere in the feature. Tone comes from the domain: the
 *  board color on SWOT walls, the impact-category legend on the grid. */
export function NoteCard({
  note,
  size = "md",
  ...rest
}: {
  note: Note;
  size?: "sm" | "md";
} & HTMLAttributes<HTMLDivElement>) {
  const tone: StickyTone =
    note.board === "impact_effort"
      ? (impactCategoryMeta(note.impact_category)?.tone ?? "yellow")
      : BOARD_META[note.board].tone;

  const flagged = note.ai_review?.status === "flagged" && !note.ai_clarification;

  return (
    <StickyNote
      title={note.title}
      description={note.description}
      footer={
        note.author_name ? (
          <span className="inline-flex items-center gap-1.5">
            {note.author_color ? <ColorDot color={note.author_color} size="xs" /> : null}
            {note.author_name}
          </span>
        ) : undefined
      }
      tone={tone}
      selected={note.selected}
      size={size}
      adornment={
        flagged || note.selected || note.promoted_objective_id ? (
          <TooltipProvider delayDuration={200}>
            {note.selected ? (
              <Star className="h-3.5 w-3.5 fill-warning text-warning" />
            ) : null}
            {flagged ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <MessageCircleQuestion className="h-3.5 w-3.5 text-warning" />
                </TooltipTrigger>
                <TooltipContent className="max-w-64 text-xs">
                  {note.ai_review?.question ?? "The AI has a question about this note."}
                </TooltipContent>
              </Tooltip>
            ) : null}
            {note.promoted_objective_id ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <CircleCheckBig className="h-3.5 w-3.5 text-success" />
                </TooltipTrigger>
                <TooltipContent className="text-xs">Promoted to an objective</TooltipContent>
              </Tooltip>
            ) : null}
          </TooltipProvider>
        ) : undefined
      }
      {...rest}
    />
  );
}
